/** Change-request list/detail/withdraw panel (GIS Phase 2A, Task 11b, spec
 *  §3.4 / §4.2 / §4.6). A "CR" is a `gis_change_requests` row — outbound
 *  proposals the queue (or manual reassignment / secondary review) sent
 *  toward GIS, structurally unrelated to a queue `Finding`.
 *
 *  List (`GET /gis-queue/crs`, filterable by kind/status/entity_type/origin)
 *  → row select → detail drawer (`GET /gis-queue/crs/{id}`, the full row) →
 *  withdraw (`POST /gis-queue/crs/{id}/withdraw`, gated `gis.cr.withdraw`
 *  ALONE, not composed with `gis.queue.view`).
 *
 *  The withdraw confirm copy is KIND-TRUTHFUL, not one blanket sentence
 *  (task-9 review H1's undo-what-creation-did principle, carried into the
 *  UI per this task's brief): `services/gis_confirmation.py::
 *  resolve_change_request` restores the entity's effective value from the
 *  GIS shadow ONLY for `attribute_fix` CRs (any origin) and `relink` CRs NOT
 *  minted by the GIS queue itself — a queue-minted relink
 *  (`propose_parent`/`keep_platform_relink`) withdraw only cancels the
 *  outbound request; its creation never wrote the column a restore would
 *  touch. `restoresEffectiveValue` below mirrors that guard verbatim. */

import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getChangeRequest, getChangeRequests, withdrawChangeRequest } from './api.ts'
import { friendlyError } from '../../utils/friendlyError.ts'
import { exportCsv } from '../../utils/csv.ts'
import { crCsvRows, csvFilename } from './worklists.ts'
import {
  CHANGE_REQUEST_ENTITY_TYPES,
  CHANGE_REQUEST_ORIGINS,
  CR_KIND_LABELS,
  CR_KINDS,
  CR_STATUS_LABELS,
  CR_STATUSES,
  hasCapability,
  type ChangeRequestDetail,
  type ChangeRequestEntityType,
  type ChangeRequestKind,
  type ChangeRequestOrigin,
  type ChangeRequestStatus,
  type ChangeRequestSummary,
  type ChangeRequestsQuery,
} from './types.ts'

const PAGE_SIZE = 25

/** Backend clamp on GET /gis-queue/crs page size (api/v1/gis_queue.py:
 *  "Page size, clamped to 500."). */
const EXPORT_LIMIT = 500

const STATUS_BADGE_STYLES: Record<string, string> = {
  open: 'bg-gray-200 text-gray-700',
  submitted: 'bg-blue-100 text-blue-700',
  confirmed: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  conflict: 'bg-amber-100 text-amber-700',
  withdrawn: 'bg-gray-100 text-gray-500',
  superseded: 'bg-purple-100 text-purple-700',
}

/** Statuses a CR can still be withdrawn FROM (services.change_requests.
 *  ALLOWED_TRANSITIONS — every status with `withdrawn` in its transition
 *  set). Pre-empts an obviously-illegal withdraw client-side; the server
 *  409 (`InvalidTransitionError`) remains the authority for a double-click
 *  race, surfaced verbatim if it ever fires anyway. */
const WITHDRAWABLE_STATUSES: ChangeRequestStatus[] = ['open', 'submitted', 'conflict']

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '—'
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString()
}

/** True when withdrawing this CR restores the entity's effective value from
 *  the GIS shadow — mirrors `resolve_change_request`'s exact guard
 *  (services/gis_confirmation.py, ~line 763): `kind` is `attribute_fix` or
 *  `relink`, the CR actually names an entity/field, and — for `relink`
 *  specifically — it was NOT minted by the GIS queue itself
 *  (`propose_parent`/`keep_platform_relink`, whose creation never wrote the
 *  column a restore would touch). Ground truth if this ever drifts. */
function restoresEffectiveValue(cr: {
  kind: string
  origin: string
  entity_type: string | null
  entity_id: number | null
  field_name: string | null
}): boolean {
  return (
    (cr.kind === 'attribute_fix' || cr.kind === 'relink') &&
    cr.entity_type != null &&
    cr.entity_id != null &&
    cr.field_name != null &&
    !(cr.kind === 'relink' && cr.origin === 'gis_queue')
  )
}

/** The withdraw confirm dialog's copy — truthful PER KIND/ORIGIN, never one
 *  blanket "restored" sentence for every kind (this task's brief, carrying
 *  forward the task-9 review H1 operator-truthfulness principle). */
function withdrawEffectCopy(cr: {
  kind: string
  origin: string
  entity_type: string | null
  entity_id: number | null
  field_name: string | null
}): string {
  if (restoresEffectiveValue(cr)) {
    const subject = cr.entity_type ? cr.entity_type.replace(/_/g, ' ') : 'entity'
    const field = cr.field_name ?? 'value'
    return `This will cancel the request and restore the ${subject}'s ${field} to GIS's last observed value.`
  }
  return 'This will cancel the outbound request. No data changes.'
}

// =============================================================================
// Filter bar
// =============================================================================

function CrFilterBar({
  filters,
  onChange,
}: {
  filters: ChangeRequestsQuery
  onChange: (next: ChangeRequestsQuery) => void
}) {
  function patch(next: Partial<ChangeRequestsQuery>) {
    onChange({ ...filters, ...next })
  }
  return (
    <div className="flex flex-wrap items-end gap-3">
      <label className="flex flex-col text-xs text-gray-600 gap-1">
        Kind
        <select
          value={filters.kind ?? ''}
          onChange={e => patch({ kind: e.target.value ? (e.target.value as ChangeRequestKind) : undefined })}
          className="text-sm border border-gray-300 rounded px-2 py-1 bg-white text-gray-700"
        >
          <option value="">Any kind</option>
          {CR_KINDS.map(k => (
            <option key={k} value={k}>
              {CR_KIND_LABELS[k]}
            </option>
          ))}
        </select>
      </label>

      <label className="flex flex-col text-xs text-gray-600 gap-1">
        Status
        <select
          value={filters.status ?? ''}
          onChange={e =>
            patch({ status: e.target.value ? (e.target.value as ChangeRequestStatus) : undefined })
          }
          className="text-sm border border-gray-300 rounded px-2 py-1 bg-white text-gray-700"
        >
          <option value="">Any status</option>
          {CR_STATUSES.map(s => (
            <option key={s} value={s}>
              {CR_STATUS_LABELS[s]}
            </option>
          ))}
        </select>
      </label>

      <label className="flex flex-col text-xs text-gray-600 gap-1">
        Entity type
        <select
          value={filters.entity_type ?? ''}
          onChange={e =>
            patch({
              entity_type: e.target.value ? (e.target.value as ChangeRequestEntityType) : undefined,
            })
          }
          className="text-sm border border-gray-300 rounded px-2 py-1 bg-white text-gray-700"
        >
          <option value="">Any entity type</option>
          {CHANGE_REQUEST_ENTITY_TYPES.map(t => (
            <option key={t} value={t}>
              {t.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
      </label>

      <label className="flex flex-col text-xs text-gray-600 gap-1">
        Origin
        <select
          value={filters.origin ?? ''}
          onChange={e =>
            patch({ origin: e.target.value ? (e.target.value as ChangeRequestOrigin) : undefined })
          }
          className="text-sm border border-gray-300 rounded px-2 py-1 bg-white text-gray-700"
        >
          <option value="">Any origin</option>
          {CHANGE_REQUEST_ORIGINS.map(o => (
            <option key={o} value={o}>
              {o.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
      </label>
    </div>
  )
}

// =============================================================================
// List table
// =============================================================================

function CrTable({
  items,
  selectedId,
  onSelect,
}: {
  items: ChangeRequestSummary[]
  selectedId: number | null
  onSelect: (id: number) => void
}) {
  if (items.length === 0) {
    return <div className="text-sm text-gray-500 py-8 text-center">No change requests match the current filters.</div>
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-left text-xs text-gray-500 uppercase tracking-wide">
            <th className="p-2">ID</th>
            <th className="p-2">Kind</th>
            <th className="p-2">Status</th>
            <th className="p-2">Entity</th>
            <th className="p-2">Field</th>
            <th className="p-2">Proposed value</th>
            <th className="p-2">Origin</th>
            <th className="p-2">Created</th>
          </tr>
        </thead>
        <tbody>
          {items.map(cr => (
            <tr
              key={cr.id}
              onClick={() => onSelect(cr.id)}
              className={`border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${
                selectedId === cr.id ? 'bg-blue-50' : ''
              }`}
            >
              <td className="p-2 font-mono text-xs">#{cr.id}</td>
              <td className="p-2 text-xs">{CR_KIND_LABELS[cr.kind as ChangeRequestKind] ?? cr.kind}</td>
              <td className="p-2">
                <span
                  className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                    STATUS_BADGE_STYLES[cr.status] ?? 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {CR_STATUS_LABELS[cr.status as ChangeRequestStatus] ?? cr.status}
                </span>
              </td>
              <td className="p-2 text-xs text-gray-600">
                {cr.entity_type ? `${cr.entity_type} #${cr.entity_id}` : '—'}
              </td>
              <td className="p-2 text-xs text-gray-600">{cr.field_name ?? '—'}</td>
              <td className="p-2 text-xs text-gray-600 max-w-[160px] truncate" title={formatValue(cr.proposed_value)}>
                {formatValue(cr.proposed_value)}
              </td>
              <td className="p-2 text-xs text-gray-600">{cr.origin.replace(/_/g, ' ')}</td>
              <td className="p-2 text-xs text-gray-400 whitespace-nowrap">{formatTimestamp(cr.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// =============================================================================
// Detail drawer + withdraw
// =============================================================================

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1 text-xs">
      <span className="text-gray-500 shrink-0">{label}</span>
      <span className="text-gray-800 text-right break-all">{value}</span>
    </div>
  )
}

function CrDetailDrawer({
  cr,
  capabilities,
  onWithdrawSuccess,
  onError,
}: {
  cr: ChangeRequestDetail
  capabilities: readonly string[]
  onWithdrawSuccess: (message: string) => void
  onError: (message: string) => void
}) {
  const [confirming, setConfirming] = useState(false)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  const missingCapability = !hasCapability(capabilities, 'gis.cr.withdraw') ? 'gis.cr.withdraw' : null
  const withdrawable = WITHDRAWABLE_STATUSES.includes(cr.status as ChangeRequestStatus)

  async function handleWithdraw() {
    setBusy(true)
    try {
      const result = await withdrawChangeRequest(cr.id, { note: note.trim() || undefined })
      onWithdrawSuccess(
        `Change request #${result.id} withdrawn${result.resolution_note ? ` — ${result.resolution_note}` : '.'}`,
      )
      setConfirming(false)
      setNote('')
    } catch (err) {
      onError(friendlyError(err, 'withdraw the change request'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-2">
      <h4 className="text-sm font-medium text-gray-700 mb-2">Change request #{cr.id}</h4>
      <DetailRow label="Kind" value={CR_KIND_LABELS[cr.kind as ChangeRequestKind] ?? cr.kind} />
      <DetailRow
        label="Status"
        value={
          <span
            className={`text-xs font-medium px-2 py-0.5 rounded-full ${
              STATUS_BADGE_STYLES[cr.status] ?? 'bg-gray-100 text-gray-700'
            }`}
          >
            {CR_STATUS_LABELS[cr.status as ChangeRequestStatus] ?? cr.status}
          </span>
        }
      />
      <DetailRow label="Entity" value={cr.entity_type ? `${cr.entity_type} #${cr.entity_id}` : '—'} />
      <DetailRow label="GIS id" value={cr.gis_id ?? '—'} />
      <DetailRow label="Source layer" value={cr.source_layer ?? '—'} />
      <DetailRow label="Target key" value={<span className="font-mono">{cr.target_key}</span>} />
      <DetailRow label="Field" value={cr.field_name ?? '—'} />
      <DetailRow label="Proposed value" value={formatValue(cr.proposed_value)} />
      <DetailRow label="Base GIS value" value={formatValue(cr.base_gis_value)} />
      <DetailRow label="Needs triage" value={cr.needs_triage ? 'Yes' : 'No'} />
      <DetailRow label="Origin" value={cr.origin.replace(/_/g, ' ')} />
      <DetailRow label="Created by" value={cr.created_by} />
      <DetailRow label="Created" value={formatTimestamp(cr.created_at)} />
      <DetailRow label="Updated" value={formatTimestamp(cr.updated_at)} />
      {cr.notes && <DetailRow label="Notes" value={cr.notes} />}
      {cr.resolution_note && <DetailRow label="Resolution note" value={cr.resolution_note} />}
      {cr.supersedes_id !== null && <DetailRow label="Supersedes" value={`#${cr.supersedes_id}`} />}
      {cr.superseded_by_id !== null && <DetailRow label="Superseded by" value={`#${cr.superseded_by_id}`} />}

      <div className="pt-2 border-t border-gray-100 mt-2">
        {!withdrawable && (
          <div className="text-xs text-gray-400">
            Cannot withdraw a request that is already {CR_STATUS_LABELS[cr.status as ChangeRequestStatus] ?? cr.status}.
          </div>
        )}
        {withdrawable && !confirming && (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            disabled={!!missingCapability}
            title={missingCapability ? `Requires the '${missingCapability}' capability` : undefined}
            className="text-xs px-3 py-1.5 rounded bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Withdraw
          </button>
        )}
        {withdrawable && confirming && (
          <div className="rounded-md border border-gray-200 bg-gray-50 p-2.5 space-y-2">
            <p className="text-xs text-gray-700">{withdrawEffectCopy(cr)}</p>
            <label className="block text-xs">
              <span className="block text-gray-600 mb-1">Note (optional)</span>
              <textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                maxLength={2000}
                rows={2}
                disabled={busy}
                className="w-full text-xs border border-gray-300 rounded px-2 py-1"
              />
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => void handleWithdraw()}
                disabled={busy}
                className="text-xs px-2.5 py-1 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                Confirm withdraw
              </button>
              <button
                type="button"
                onClick={() => {
                  setConfirming(false)
                  setNote('')
                }}
                disabled={busy}
                className="text-xs px-2.5 py-1 rounded bg-white border border-gray-300 text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// =============================================================================
// Main panel
// =============================================================================

interface CrPanelProps {
  capabilities: readonly string[]
}

export function CrPanel({ capabilities }: CrPanelProps) {
  const queryClient = useQueryClient()
  const [filters, setFilters] = useState<ChangeRequestsQuery>({})
  const [offset, setOffset] = useState(0)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [feedback, setFeedback] = useState<{ tone: 'success' | 'error'; message: string } | null>(null)
  const [exporting, setExporting] = useState(false)

  const handleFiltersChange = (next: ChangeRequestsQuery) => {
    setFilters(next)
    setOffset(0)
    setSelectedId(null)
  }

  const query = { ...filters, limit: PAGE_SIZE, offset }

  const {
    data,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['gis-queue', 'crs', query],
    queryFn: () => getChangeRequests(query),
    enabled: hasCapability(capabilities, 'gis.queue.view'),
  })

  const detailQuery = useQuery({
    queryKey: ['gis-queue', 'cr-detail', selectedId],
    queryFn: () => getChangeRequest(selectedId as number),
    enabled: selectedId != null && hasCapability(capabilities, 'gis.queue.view'),
  })

  if (!hasCapability(capabilities, 'gis.queue.view')) {
    return (
      <div className="text-sm text-gray-600">
        You don&apos;t have access to change requests. Ask an administrator to grant the{' '}
        <code className="px-1 bg-gray-200 rounded">gis.queue.view</code> capability.
      </div>
    )
  }

  const items = data?.items ?? []
  const total = data?.total ?? 0

  function handleWithdrawSuccess(message: string) {
    setFeedback({ tone: 'success', message })
    void queryClient.invalidateQueries({ queryKey: ['gis-queue', 'crs'] })
    void queryClient.invalidateQueries({ queryKey: ['gis-queue', 'cr-detail', selectedId] })
  }

  function handleError(message: string) {
    if (!message) return
    setFeedback({ tone: 'error', message })
  }

  async function handleExportCrs() {
    setExporting(true)
    try {
      // Fresh fetch of the CURRENT FILTER's rows at the backend clamp — the
      // export is the matched set, never just the visible page.
      const page = await getChangeRequests({ ...filters, limit: EXPORT_LIMIT, offset: 0 })
      exportCsv(csvFilename('gis-change-requests'), crCsvRows(page.items))
      setFeedback({
        tone: 'success',
        message:
          page.total > page.items.length
            ? `Exported the first ${page.items.length} of ${page.total} change requests — narrow the filters to export the rest.`
            : `Exported ${page.items.length} change request${page.items.length === 1 ? '' : 's'} to CSV.`,
      })
    } catch (err) {
      setFeedback({ tone: 'error', message: friendlyError(err, 'export the change requests') })
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
        <CrFilterBar filters={filters} onChange={handleFiltersChange} />
      </div>

      {feedback && (
        <div
          role={feedback.tone === 'error' ? 'alert' : 'status'}
          className={`text-sm rounded-md px-3 py-2 border flex items-center justify-between ${
            feedback.tone === 'error'
              ? 'text-red-700 bg-red-50 border-red-200'
              : 'text-green-700 bg-green-50 border-green-200'
          }`}
        >
          <span>{feedback.message}</span>
          <button type="button" onClick={() => setFeedback(null)} className="ml-3 text-xs underline shrink-0">
            Dismiss
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-700">
              Change requests <span className="text-gray-400 ml-1">({total})</span>
            </h3>
            <button
              type="button"
              onClick={() => void handleExportCrs()}
              disabled={exporting || total === 0}
              className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exporting ? 'Exporting…' : 'Export CSV'}
            </button>
          </div>

          {isLoading && <div className="text-sm text-gray-500 py-8 text-center">Loading...</div>}
          {isError && (
            <div className="text-sm text-red-600 py-8 text-center">
              {friendlyError(error, 'load change requests')}
            </div>
          )}
          {!isLoading && !isError && (
            <>
              <CrTable items={items} selectedId={selectedId} onSelect={setSelectedId} />
              {total > PAGE_SIZE && (
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => setOffset(o => Math.max(0, o - PAGE_SIZE))}
                    disabled={offset === 0}
                    className="text-sm px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span className="text-xs text-gray-500">
                    {offset + 1}-{Math.min(offset + PAGE_SIZE, total)} of {total}
                  </span>
                  <button
                    type="button"
                    onClick={() => setOffset(o => o + PAGE_SIZE)}
                    disabled={offset + PAGE_SIZE >= total}
                    className="text-sm px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="lg:col-span-1">
          {selectedId == null && (
            <div className="text-sm text-gray-500 py-8 text-center bg-white border border-gray-200 rounded-lg">
              Select a change request to see its detail.
            </div>
          )}
          {selectedId != null && detailQuery.isLoading && (
            <div className="text-sm text-gray-500 py-8 text-center bg-white border border-gray-200 rounded-lg">
              Loading...
            </div>
          )}
          {selectedId != null && detailQuery.isError && (
            <div role="alert" className="text-sm text-red-600 py-8 text-center bg-white border border-gray-200 rounded-lg">
              {friendlyError(detailQuery.error, 'load the change request')}
            </div>
          )}
          {selectedId != null && detailQuery.data && (
            <CrDetailDrawer
              cr={detailQuery.data}
              capabilities={capabilities}
              onWithdrawSuccess={handleWithdrawSuccess}
              onError={handleError}
            />
          )}
        </div>
      </div>
    </div>
  )
}
